(function (root, factory) {
  const api = factory();
  if (typeof module === 'object' && module.exports) module.exports = api;
  if (root) root.CityOpticsDryEyeFinderCore = api;
})(typeof globalThis !== 'undefined' ? globalThis : this, function () {
  'use strict';

  const CATEGORY_ORDER = [
    'Everyday hydration',
    'Evaporation relief',
    'Long-lasting relief',
    'Overnight protection',
    'Eyelid care',
  ];

  const RESULT_COPY = {
    'Everyday hydration': 'A hydrating lubricant may be a suitable starting point for dry, gritty or tired eyes.',
    'Evaporation relief':
      'Your symptoms appear worse in conditions that can increase tear evaporation. A lipid-based lubricant may provide a suitable starting point.',
    'Long-lasting relief':
      'A longer-lasting formulation may be helpful when ordinary drops do not provide sufficient duration of relief.',
    'Overnight protection':
      'A longer-lasting night-time product may be suitable when discomfort is worse on waking or overnight.',
    'Eyelid care':
      'An eyelid-care product may be a suitable starting point when discomfort is concentrated around the eyelids or eyelashes.',
  };

  const QUESTIONS = [
    {
      id: 'symptoms',
      title: 'Which symptoms sound most like yours?',
      help: 'Select up to two.',
      type: 'multiple',
      maxSelections: 2,
      options: [
        { id: 'dry_gritty_tired', label: 'Dry, gritty or tired eyes', category: 'Everyday hydration', score: 3 },
        {
          id: 'burning_watering_environment',
          label: 'Burning or watering, especially in wind or air conditioning',
          category: 'Evaporation relief',
          score: 3,
        },
        {
          id: 'drops_not_lasting',
          label: 'Relief from ordinary drops does not last long enough',
          category: 'Long-lasting relief',
          score: 3,
        },
        {
          id: 'waking_overnight',
          label: 'Dryness on waking or discomfort overnight',
          category: 'Overnight protection',
          score: 3,
        },
        {
          id: 'eyelid_discomfort',
          label: 'Crusty, irritated or uncomfortable eyelids',
          category: 'Eyelid care',
          score: 3,
        },
        { id: 'mainly_itchy', label: 'Mainly itchy eyes' },
      ],
    },
    {
      id: 'timing',
      title: 'When are your symptoms usually worst?',
      help: 'Choose the pattern that best matches your experience.',
      type: 'single',
      options: [
        {
          id: 'screens_close_work',
          label: 'During screens, reading or close work',
          category: 'Everyday hydration',
          score: 2,
        },
        {
          id: 'outdoors_air_conditioning',
          label: 'Outdoors, in wind or air conditioning',
          category: 'Evaporation relief',
          score: 2,
        },
        {
          id: 'most_of_day',
          label: 'Throughout most of the day',
          category: 'Long-lasting relief',
          score: 2,
        },
        {
          id: 'waking_or_overnight',
          label: 'On waking or overnight',
          category: 'Overnight protection',
          score: 2,
        },
        {
          id: 'eyelids_eyelashes',
          label: 'Around my eyelids or eyelashes',
          category: 'Eyelid care',
          score: 2,
        },
        { id: 'no_clear_pattern', label: 'There is no clear pattern' },
      ],
    },
    {
      id: 'usage',
      title: 'How often do you use—or expect to use—eye drops?',
      help: 'This helps us apply product features only when they are needed.',
      type: 'single',
      options: [
        { id: 'occasionally', label: 'Occasionally' },
        { id: 'one_to_three', label: 'Once to three times per day' },
        { id: 'four_or_more', label: 'Four or more times per day' },
        { id: 'not_sure', label: 'I’m not sure' },
      ],
    },
    {
      id: 'contacts',
      title: 'Do you need to use the product while wearing contact lenses?',
      help: 'Only products explicitly marked as compatible can pass this requirement.',
      type: 'single',
      options: [
        { id: 'yes_lenses_in', label: 'Yes, while my lenses are in' },
        { id: 'remove_first', label: 'No, I can remove my lenses first' },
        { id: 'no_contacts', label: 'I don’t wear contact lenses' },
      ],
    },
    {
      id: 'safety',
      title: 'Are you experiencing any of the following?',
      help: 'Select all that apply. Choose “None of these” if none apply.',
      type: 'multiple',
      options: [
        {
          id: 'eye_pain',
          label: 'Sudden, severe or worsening eye pain',
          detail: 'This does not include mild burning, stinging or grittiness associated with dryness.',
        },
        { id: 'vision_change', label: 'A sudden change in vision' },
        { id: 'light_sensitivity', label: 'Unusual sensitivity to light' },
        { id: 'significant_redness', label: 'Significant or rapidly worsening redness' },
        { id: 'thick_discharge', label: 'Thick discharge or pus' },
        { id: 'injury_chemical', label: 'A recent eye injury or chemical exposure' },
        { id: 'none', label: 'None of these', exclusive: true },
      ],
    },
  ];

  const questionById = (id) => QUESTIONS.find((question) => question.id === id);
  const optionById = (questionId, optionId) => questionById(questionId)?.options.find((option) => option.id === optionId);
  const answerList = (value) => (Array.isArray(value) ? value : value ? [value] : []);

  function hasValidAnswer(question, answers) {
    const selected = answerList(answers[question.id]);
    if (selected.length === 0 || selected.some((id) => !question.options.some((option) => option.id === id))) return false;
    if (new Set(selected).size !== selected.length) return false;
    if (question.type === 'single' && selected.length !== 1) return false;
    if (question.maxSelections && selected.length > question.maxSelections) return false;
    const exclusiveIds = question.options.filter((option) => option.exclusive).map((option) => option.id);
    if (selected.length > 1 && selected.some((id) => exclusiveIds.includes(id))) return false;
    return true;
  }

  function calculateResult(answers) {
    const safetyAnswers = [...new Set(answerList(answers.safety))];
    const safetyFlags = safetyAnswers.filter((id) => id !== 'none');
    if (safetyFlags.length > 0) {
      return {
        type: 'safety',
        chemicalExposure: safetyFlags.includes('injury_chemical'),
        safetyFlags,
      };
    }

    const symptomAnswers = [...new Set(answerList(answers.symptoms))];
    if (symptomAnswers.length === 1 && symptomAnswers[0] === 'mainly_itchy') {
      return { type: 'itching' };
    }

    const scores = Object.fromEntries(CATEGORY_ORDER.map((category) => [category, 0]));
    const symptomOptions = symptomAnswers.map((id) => optionById('symptoms', id)).filter(Boolean);
    const timingOption = optionById('timing', answers.timing);
    [...symptomOptions, timingOption].forEach((option) => {
      if (option?.category && option.score) scores[option.category] += option.score;
    });

    const ranked = CATEGORY_ORDER.map((category, index) => ({ category, score: scores[category], index })).sort(
      (a, b) => b.score - a.score || a.index - b.index
    );
    const highestScore = ranked[0]?.score || 0;

    const primaryCategories = ranked
      .filter((item) => item.score === highestScore && item.score > 0)
      .map((item) => item.category);
    const primary = primaryCategories[0] || CATEGORY_ORDER[0];
    const secondaryCandidate = ranked.find((item) => !primaryCategories.includes(item.category) && item.score > 0);
    const secondary = secondaryCandidate && highestScore - secondaryCandidate.score <= 1 ? secondaryCandidate.category : null;

    return {
      type: 'standard',
      primary,
      primaryCategories: primaryCategories.length ? primaryCategories : [primary],
      secondary,
      scores,
      copy: RESULT_COPY[primary],
      itchingAdvisory: symptomAnswers.includes('mainly_itchy') && symptomAnswers.length > 1,
      requirePreservativeFree: answers.usage === 'four_or_more',
      prioritizePreservativeFree: answers.usage === 'one_to_three',
      requireContactLensCompatible: answers.contacts === 'yes_lenses_in',
    };
  }

  function filterProducts(products, result) {
    if (!result || result.type !== 'standard') return [];
    const matchingCategories = Array.isArray(result.primaryCategories) && result.primaryCategories.length
      ? result.primaryCategories
      : [result.primary];
    const eligible = products.filter((product) => {
      const bestFor = Array.isArray(product.bestFor) ? product.bestFor : [];
      const keyFeatures = Array.isArray(product.keyFeatures) ? product.keyFeatures : [];
      if (!matchingCategories.some((category) => bestFor.includes(category))) return false;
      if (result.requirePreservativeFree && product.preservativeFree !== true) return false;
      if (result.requireContactLensCompatible && !keyFeatures.includes('Contact lens compatible')) return false;
      return true;
    });

    if (!result.prioritizePreservativeFree) return eligible;
    return [
      ...eligible.filter((product) => product.preservativeFree === true),
      ...eligible.filter((product) => product.preservativeFree !== true),
    ];
  }

  function buildFilterUrl(collectionUrl, result) {
    const url = new URL(collectionUrl, 'https://cityoptics.com.au');
    url.search = '';
    url.searchParams.set('sort_by', 'manual');
    const matchingCategories = Array.isArray(result.primaryCategories) && result.primaryCategories.length
      ? result.primaryCategories
      : [result.primary];
    matchingCategories.forEach((category) => url.searchParams.append('filter.p.m.custom.dry_eye_best_for', category));
    if (result.requirePreservativeFree) url.searchParams.set('filter.p.m.custom.preservative_free', '1');
    if (result.requireContactLensCompatible) {
      url.searchParams.set('filter.p.m.custom.dry_eye_key_features', 'Contact lens compatible');
    }
    return `${url.pathname}${url.search}`;
  }

  return {
    CATEGORY_ORDER,
    QUESTIONS,
    RESULT_COPY,
    hasValidAnswer,
    calculateResult,
    filterProducts,
    buildFilterUrl,
  };
});
